/**
 * RibbonProgress — shared progress bar for the Lead-to-AE ribbon.
 * Used at the top of AddLead, RouteBdr, LeadQualify, LeadAssign, and LeadSuccess.
 *
 * P0-5: Expanded to 5 steps to surface the BDR routing step that
 * was previously bypassed. Standard funnel: MQL → BDR → SQL → AE.
 *
 * Visual: colored bar fills left-to-right as steps complete.
 * Brand: black fill, slate pending, red accent on active dot.
 */

const STEPS = [
  { id: 1, label: "Add lead" },
  { id: 2, label: "Route to BDR" },
  { id: 3, label: "Qualify" },
  { id: 4, label: "Assign AE" },
  { id: 5, label: "Done" },
];

export function RibbonProgress({ step }: { step: 1 | 2 | 3 | 4 | 5 }) {
  return (
    <div className="max-w-3xl mx-auto px-5 py-3">
      {/* Progress bar */}
      <div className="relative flex items-center">
        {/* Track */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-neutral-200" />
        {/* Fill */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-0.5 bg-[#0B0B0F] transition-all duration-500 ease-out left-0"
          style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
        />

        {/* Step dots */}
        <div className="relative z-10 flex items-center justify-between w-full">
          {STEPS.map((s) => {
            const done    = step > s.id;
            const active  = step === s.id;
            return (
              <div key={s.id} className="flex flex-col items-center gap-1">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                    done
                      ? "bg-[#0B0B0F]"
                      : active
                      ? "bg-[#DC2626] ring-2 ring-[#DC2626]/25"
                      : "bg-white border border-neutral-300"
                  }`}
                >
                  {done ? (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                      <path d="M2 5l2.5 2.5 3.5-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : active ? (
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  ) : (
                    <span className="text-[8px] font-bold text-neutral-500">{s.id}</span>
                  )}
                </div>
                <span
                  className={`text-[9px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap transition-colors ${
                    done ? "text-[#0B0B0F]" : active ? "text-[#DC2626]" : "text-neutral-500"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
